import { CollectionCards } from "./collection-cards";
import { Suspense, use } from "react";
import { Link } from "@tanstack/react-router";
import { JsonLd } from "@/components/seo/json-ld";
import { marketing } from "../lib/collections";
import type { ContentPageData } from "../lib/server";
import { getMDXComponents } from "./mdx";

function Body({ data }: { data: ContentPageData }) {
  const page = marketing.getPage(data.path);
  if (!page) throw new Error(`Unknown marketing page: ${data.path}`);
  use(page.load());
  const MDX = page.body;
  const isHub = data.url === "/compare";
  return (
    <main id="marketing-main" className="mx-auto max-w-7xl px-6 md:px-10">
      {data.schemas.length > 0 && <JsonLd data={data.schemas.flat()} />}
      <section className="border-b border-hb-card py-16 md:py-24">
        <p className="font-mono-hb mb-6 text-xs uppercase tracking-widest text-hb-accent">
          {isHub ? (
            "Find your email infrastructure"
          ) : (
            <Link to="/compare" preload="intent">
              All comparisons /
            </Link>
          )}
        </p>
        <h1 className="max-w-5xl text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
          {data.title}
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-hb-muted md:text-xl">
          {data.description}
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/login"
            preload="intent"
            className="rounded-md bg-hb-accent px-6 py-3 text-sm font-bold text-hb-accent-fg hover:opacity-90"
          >
            Start building for free
          </Link>
          <Link
            to="/docs"
            preload="intent"
            className="rounded-md border border-hb-card px-6 py-3 text-sm font-semibold hover:bg-hb-raised"
          >
            Read the documentation
          </Link>
        </div>
      </section>
      <article
        aria-label={data.title}
        className={`marketing-prose prose min-w-0 max-w-none py-12 ${isHub ? "comparison-hub" : "comparison-detail"}`}
      >
        <MDX
          components={getMDXComponents({
            CollectionCards: () => <CollectionCards cards={data.cards} />,
          })}
        />
      </article>
      <section
        className="rounded-xl border border-hb-card bg-hb-surface p-8 md:p-12"
        aria-label="Get started with Bavimail"
      >
        <h2 className="text-2xl font-bold md:text-3xl">Build with Bavimail</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-hb-muted">
          Send, receive, and manage email from your application. Explore the API or create your
          account to get started.
        </p>
        <Link
          to="/login"
          preload="intent"
          className="mt-6 inline-flex rounded-md bg-hb-accent px-6 py-3 text-sm font-bold text-hb-accent-fg"
        >
          Start free
        </Link>
      </section>
    </main>
  );
}

export function MarketingPage({ data }: { data: ContentPageData }) {
  return (
    <Suspense fallback={<div className="p-8 text-hb-muted">Loading comparison...</div>}>
      <Body data={data} />
    </Suspense>
  );
}
